import React, { useMemo, useState } from 'react';
import { getClasses } from '@pasqal/core/helpers/styles';
import { clamp } from '~/util';
import type { SelectOption } from '~/util';
import './ListSelect.css';

interface ListSelectProps<V = unknown> {
  name?: string;
  value: V;
  onChange: (val: V) => void;
  options: SelectOption<V>[];
  disabled?: boolean;
  onStateChange?: (val: boolean) => void;
  className?: string;
  style?: React.CSSProperties;
}

export default function ListSelect<V = unknown>({
  name,
  value,
  onChange,
  options,
  disabled,
  onStateChange,
  className,
  style
}: ListSelectProps<V>) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => {
    const index = options.findIndex((o) => o.value === value);
    const option = index !== -1 ? options[index] : { label: value, value };
    return { ...option, index };
  }, [options, value]);

  function handleClose() {
    setOpen(false);
    onStateChange?.(false);
  }

  function handleToggle() {
    const next = !open;
    setOpen(next);
    onStateChange?.(next);
  }

  function handleSelectOption(index: number) {
    const option = options[index];
    if (option !== null && option.value !== value) {
      onChange(option.value);
    }
    handleClose();
  }

  function emitSelectIndex(index: number) {
    const option = options[index];
    if (option !== null && option.value !== value) {
      onChange(option.value);
    }
  }

  function handleKeydown(e: React.KeyboardEvent, optionIndex?: number) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        if (optionIndex !== undefined) {
          handleSelectOption(optionIndex);
        }
        break;
      case 'Escape':
        handleClose();
        break;
      case 'ArrowUp':
        emitSelectIndex(clamp(selected.index - 1, 0, options.length - 1));
        break;
      case 'ArrowDown':
        emitSelectIndex(clamp(selected.index + 1, 0, options.length - 1));
        break;
    }
  }

  const css = getClasses(['ListSelect-wrapper', className]);

  return (
    <div
      className={css}
      style={style}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          handleClose();
        }
      }}
    >
      <button
        id={name}
        className={`ListSelect${open ? ' is-open' : ''}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeydown}
      >
        {String(selected.label)}
        <span className="ListSelect-chevron" />
      </button>
      {open && (
        <ul className="ListSelect-options" role="listbox" tabIndex={-1}>
          {options.map((option, index) => (
            <li
              key={index}
              aria-selected={index === selected.index}
              className="ListSelect-option"
              tabIndex={0}
              role="option"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleSelectOption(index);
              }}
              onKeyDown={(e) => handleKeydown(e, index)}
            >
              {String(option.label)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
